import React, { useEffect, useState } from 'react';
import { Clock, Calendar, DollarSign, CheckCircle, AlertCircle, ListTodo, TrendingUp, Sparkles, Star, Target } from 'lucide-react';
import { DashboardCard } from './DashboardCard.tsx';
import { DashboardMetric } from './DashboardMetric.tsx';
import { TaskList } from './TaskList.tsx';
import { BudgetSummary } from './BudgetSummary.tsx';
import { useTaskContext } from '../../../context/TaskContext.tsx';
import { useBudgetContext } from '../../../context/BudgetContext.tsx';
import './dashboard.css';
import { getTasks } from '../../../utils/tasks.ts';
import { getBudget } from '../../../utils/budget.ts';

interface DashboardViewProps {
  onNavigate: (view: 'dashboard' | 'task-board' | 'calendar' | 'budget' | 'wedding-plan') => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({ onNavigate }) => {
  const [tasks, setTasks] = useState({
    todoTasks: [],
    inProgressTasks: [],
    completedTasks: [],
  });
  const [budget, setBudget] = useState<number>(0);

  useEffect(() => {
    const fetchTasksAndBudget = async () => {
      const allTasks = await getTasks();
      const getRecentTasks = (status: string) => allTasks
        .filter(task => task.status === status)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 3);

      const todoTasks = getRecentTasks('TODO');
      const inProgressTasks = getRecentTasks('IN_PROGRESS');
      const completedTasks = getRecentTasks('DONE');

      setTasks({ todoTasks, inProgressTasks, completedTasks });

      try {
        const budgetSections = await getBudget();
        const totalCost = budgetSections.reduce((acc, item) => acc + (item.cost || 0), 0);
        if (totalCost !== null) {
          setBudget(totalCost);
        }
      } catch (error) {
        console.error('Error fetching budget:', error);
      }
    };
    fetchTasksAndBudget();
  }, []);

  const totalTasks = tasks.todoTasks.length + tasks.inProgressTasks.length + tasks.completedTasks.length;
  const highPriorityTasks = tasks.todoTasks.filter(task => task.priority === 'high').length;
  const completionRate = totalTasks > 0 ? ((tasks.completedTasks.length / totalTasks) * 100).toFixed(0) : 0;
  
  return (
    <div className="min-h-screen bg-white p-6 pt-20 space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 via-rose-500/10 to-orange-500/10 rounded-3xl"></div>
        <div className="relative bg-white/40 backdrop-blur-xl rounded-3xl border border-white/60 shadow-2xl p-8">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-500 rounded-2xl shadow-lg">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                  Wedding Dashboard
                </h1>
              </div>
              <p className="text-gray-600 text-lg">Track your dream wedding planning journey</p>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{completionRate}%</div>
                <div className="text-sm text-gray-600">Complete</div>
              </div>
              <div className="w-16 h-16 relative">
                <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="rgba(255, 105, 180, 0.2)"
                    strokeWidth="3"
                  />
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="url(#progress-gradient)"
                    strokeWidth="3"
                    strokeDasharray={`${completionRate}, 100`}
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="progress-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FF69B4" />
                      <stop offset="100%" stopColor="#FF6347" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="group relative overflow-hidden bg-white/30 backdrop-blur-xl rounded-2xl border border-white/40 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/10"></div>
          <div className="relative p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <ListTodo className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Total Tasks</p>
                <p className="text-3xl font-bold text-gray-900">{totalTasks}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="group relative overflow-hidden bg-white/30 backdrop-blur-xl rounded-2xl border border-white/40 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-pink-500/10"></div>
          <div className="relative p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Star className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">High Priority</p>
                <p className="text-3xl font-bold text-gray-900">{highPriorityTasks}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="group relative overflow-hidden bg-white/30 backdrop-blur-xl rounded-2xl border border-white/40 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10"></div>
          <div className="relative p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Completed</p>
                <p className="text-3xl font-bold text-gray-900">{tasks.completedTasks.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="group relative overflow-hidden bg-white/30 backdrop-blur-xl rounded-2xl border border-white/40 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10"></div>
          <div className="relative p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Total Budget</p>
                <p className="text-3xl font-bold text-gray-900">${budget.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
        {/* Task Management - Takes 3 columns */}
        <div className="xl:col-span-3 space-y-8">
          {/* Task Lists Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* To Do Tasks */}
            <div className="group relative overflow-hidden bg-white/30 backdrop-blur-xl rounded-2xl border border-white/40 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5"></div>
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-md">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-900">To Do</h4>
                  </div>
                  <div className="px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm rounded-full font-bold shadow-md">
                    {tasks.todoTasks.length}
                  </div>
                </div>
                <TaskList 
                  tasks={tasks.todoTasks} 
                  emptyMessage="No upcoming tasks"
                  onNavigateToTaskboard={() => onNavigate('task-board')}
                  compact
                />
              </div>
            </div>

            {/* In Progress Tasks */}
            <div className="group relative overflow-hidden bg-white/30 backdrop-blur-xl rounded-2xl border border-white/40 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-amber-500/5"></div>
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg shadow-md">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-900">In Progress</h4>
                  </div>
                  <div className="px-3 py-1 bg-gradient-to-r from-orange-500 to-amber-600 text-white text-sm rounded-full font-bold shadow-md">
                    {tasks.inProgressTasks.length}
                  </div>
                </div>
                <TaskList 
                  tasks={tasks.inProgressTasks} 
                  emptyMessage="No pending tasks"
                  onNavigateToTaskboard={() => onNavigate('task-board')}
                  compact
                />
              </div>
            </div>

            {/* Completed Tasks */}
            <div className="group relative overflow-hidden bg-white/30 backdrop-blur-xl rounded-2xl border border-white/40 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-500/5"></div>
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg shadow-md">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-900">Completed</h4>
                  </div>
                  <div className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm rounded-full font-bold shadow-md">
                    {tasks.completedTasks.length}
                  </div>
                </div>
                <TaskList 
                  tasks={tasks.completedTasks} 
                  emptyMessage="No completed tasks"
                  onNavigateToTaskboard={() => onNavigate('task-board')}
                  compact
                />
              </div>
            </div>
          </div>

          {/* Progress Overview */}
          <div className="relative overflow-hidden bg-white/30 backdrop-blur-xl rounded-2xl border border-white/40 shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-rose-500/5"></div>
            <div className="relative p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl shadow-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-900 text-xl">Progress Overview</h4>
                </div>
                <div className="hidden md:block">
                  <button 
                    onClick={() => onNavigate('task-board')}
                    className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-semibold rounded-xl shadow-lg hover:scale-105 transition-transform duration-200"
                  >
                    View All Tasks
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center bg-white/40 backdrop-blur-xl rounded-2xl p-6 border border-white/60">
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3">
                    {completionRate}%
                  </div>
                  <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Completion Rate</div>
                </div>
                <div className="text-center bg-white/40 backdrop-blur-xl rounded-2xl p-6 border border-white/60">
                  <div className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-3">
                    {tasks.inProgressTasks.length}
                  </div>
                  <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Active Tasks</div>
                </div>
                <div className="text-center bg-white/40 backdrop-blur-xl rounded-2xl p-6 border border-white/60">
                  <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-3">
                    {tasks.completedTasks.length}
                  </div>
                  <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Completed</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Overview - Takes 1 column */}
        <div className="relative overflow-hidden bg-white/30 backdrop-blur-xl rounded-2xl border border-white/40 shadow-xl">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5"></div>
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg shadow-md">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <h4 className="font-bold text-gray-900">Budget Overview</h4>
              </div>
              <button 
                onClick={() => onNavigate('budget')}
                className="p-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg shadow-md hover:scale-110 transition-transform duration-200"
              >
                <Target className="w-4 h-4" />
              </button>
            </div>
            <BudgetSummary onNavigateToBudget={() => onNavigate('budget')} />
          </div>
        </div>
      </div>
    </div>
  );
};